import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDebtPlan, useGenerateDebtPlan, useUpdateDebtGoal } from '../hooks/useDebt';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import AIInsights from '../components/plans/AIInsights';
import DebtForm from '../components/debt/DebtForm';

function fmt(value, currency = 'USD') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  });
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className={`card ${highlight ? 'border-brand-700/50 bg-brand-950/20' : ''}`}>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${highlight ? 'text-brand-400' : 'text-slate-100'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const STRATEGY_META = {
  avalanche: { label: 'Avalanche', emoji: '🏔️', color: 'text-sky-400', bg: 'bg-sky-950/30 border-sky-700/40' },
  snowball:  { label: 'Snowball',  emoji: '⛄', color: 'text-violet-400', bg: 'bg-violet-950/30 border-violet-700/40' },
  fixed:     { label: 'Fixed',    emoji: '📌', color: 'text-amber-400', bg: 'bg-amber-950/30 border-amber-700/40' },
};

function StrategyBadge({ strategy }) {
  const meta = STRATEGY_META[strategy] || STRATEGY_META.fixed;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${meta.color} ${meta.bg}`}>
      {meta.emoji} {meta.label}
    </span>
  );
}

function AmortizationTable({ projection, currency }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? projection : projection.slice(0, 12);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-200">{t('debt.plan.table.title')}</h3>
        <span className="text-xs text-slate-500">{projection.length} {t('debt.plan.table.months_total')}</span>
      </div>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm min-w-[28rem]">
          <thead>
            <tr className="border-b border-surface-card">
              <th className="text-left text-xs text-slate-500 pb-2 pl-4 sm:pl-0 font-medium">{t('debt.plan.table.month')}</th>
              <th className="text-right text-xs text-slate-500 pb-2 font-medium">{t('debt.plan.table.payment')}</th>
              <th className="text-right text-xs text-slate-500 pb-2 font-medium">{t('debt.plan.table.principal')}</th>
              <th className="text-right text-xs text-slate-500 pb-2 font-medium">{t('debt.plan.table.interest')}</th>
              <th className="text-right text-xs text-slate-500 pb-2 pr-4 sm:pr-0 font-medium">{t('debt.plan.table.balance')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.month} className="border-b border-surface-card/50 hover:bg-surface/30 transition-colors">
                <td className="py-2 text-slate-400 pl-4 sm:pl-0">#{row.month}</td>
                <td className="py-2 text-right text-slate-300">{fmt(row.payment, currency)}</td>
                <td className="py-2 text-right text-green-400">{fmt(row.principal_paid, currency)}</td>
                <td className="py-2 text-right text-red-400">{fmt(row.interest_paid, currency)}</td>
                <td className="py-2 text-right text-slate-300 pr-4 sm:pr-0">{fmt(row.balance, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {projection.length > 12 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 text-sm text-brand-400 hover:text-brand-300 transition-colors"
        >
          {expanded ? t('common.show_less') : t('debt.plan.table.show_all').replace('{n}', projection.length)}
        </button>
      )}
    </div>
  );
}

export default function DebtPlanPage() {
  const { id } = useParams();
  const t = useT();
  const { user, isPaid } = useAuthStore();
  const paid = isPaid();
  const currency = user?.currency || 'USD';

  const { data, isLoading } = useDebtPlan(id);
  const { mutateAsync: generatePlan, isPending: isGenerating } = useGenerateDebtPlan();
  const { mutateAsync: updateGoal, isPending: isUpdating } = useUpdateDebtGoal();
  const [showRegenModal, setShowRegenModal] = useState(false);

  const goal = data?.goal;
  const plan = data?.plan;

  async function handleGenerate() {
    await generatePlan(id);
  }

  async function handleRegenWithUpdate(formData) {
    await updateGoal({ id, ...formData });
    setShowRegenModal(false);
    await generatePlan(id);
  }

  async function handleRegenSame() {
    setShowRegenModal(false);
    await generatePlan(id);
  }

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-48 bg-surface-card rounded" />
        <div className="grid md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-28 bg-surface-card rounded-2xl" />)}
        </div>
        <div className="h-64 bg-surface-card rounded-2xl" />
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="card text-center py-16">
        <p className="text-slate-400">{t('debt.plan.not_found')}</p>
        <Link to="/debt" className="mt-4 inline-block btn-primary">{t('common.go_back')}</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Breadcrumb */}
      <div>
        <Link to="/debt" className="text-sm text-slate-500 hover:text-brand-400 transition-colors">
          ← {t('debt.plan.back')}
        </Link>
        <h1 className="text-2xl font-bold text-slate-100 mt-2">{goal.title}</h1>
        <p className="text-slate-400 mt-1">
          {t(`debt.type.${goal.debt_type}`)} · {fmt(goal.total_debt, currency)} {t('debt.plan.at_rate').replace('{rate}', parseFloat(goal.interest_rate || 0).toFixed(2))}
        </p>
      </div>

      {/* No plan yet */}
      {!plan && (
        <div className="card text-center py-16">
          <p className="text-5xl mb-4">🤖</p>
          <p className="text-slate-300 font-semibold text-lg">{t('debt.plan.empty.title')}</p>
          <p className="text-slate-500 text-sm mt-2 mb-8">{t('debt.plan.empty.sub')}</p>
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? t('debt.plan.generating') : t('debt.plan.generate_cta')}
          </Button>
        </div>
      )}

      {/* Generating */}
      {plan?.status === 'generating' && (
        <div className="card text-center py-12">
          <div className="flex justify-center mb-4">
            <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-slate-300 font-semibold">{t('debt.plan.generating_title')}</p>
          <p className="text-slate-500 text-sm mt-2">{t('debt.plan.generating_sub')}</p>
        </div>
      )}

      {/* Failed */}
      {plan?.status === 'failed' && (
        <div className="rounded-xl border border-red-700/50 bg-red-950/30 p-6 flex items-center justify-between gap-4">
          <div>
            <p className="text-red-400 font-semibold">{t('debt.plan.failed.title')}</p>
            <p className="text-red-300/70 text-sm mt-1">{plan.failure_reason || t('debt.plan.failed.sub')}</p>
          </div>
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? t('debt.plan.generating') : t('debt.plan.retry_cta')}
          </Button>
        </div>
      )}

      {/* Active plan */}
      {plan?.status === 'active' && (
        <>
          {/* Strategy badge */}
          <div className="flex items-center gap-3">
            <StrategyBadge strategy={plan.strategy} />
            <span className="text-xs text-slate-500">{t('debt.plan.strategy_label')}</span>
          </div>

          {/* Key stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label={t('debt.plan.stat.monthly_payment')}
              value={fmt(plan.monthly_payment, currency)}
              sub={t('debt.plan.stat.monthly_payment_sub')}
              highlight
            />
            <StatCard
              label={t('debt.plan.stat.months_to_payoff')}
              value={plan.months_to_payoff}
              sub={t('debt.plan.stat.months_to_payoff_sub').replace(
                '{yr}',
                (plan.months_to_payoff / 12).toFixed(1)
              )}
            />
            <StatCard
              label={t('debt.plan.stat.total_interest')}
              value={fmt(plan.total_interest, currency)}
              sub={t('debt.plan.stat.total_interest_sub')}
            />
            <StatCard
              label={t('debt.plan.stat.total_paid')}
              value={fmt(plan.total_paid, currency)}
              sub={t('debt.plan.stat.total_paid_sub')}
            />
          </div>

          {/* Summary */}
          {plan.summary && (
            <div className="card">
              <h3 className="font-semibold text-slate-200 mb-2">{t('debt.plan.summary.title')}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{plan.summary}</p>
            </div>
          )}

          {/* Recommendations */}
          {(plan.recommendations?.length > 0 || plan.risk_warnings?.length > 0) && (
            <div className="card">
              <h3 className="font-semibold text-slate-200 mb-3">{t('debt.plan.recommendations.title')}</h3>
              <AIInsights
                recommendations={plan.recommendations || []}
                riskWarnings={[]}
                currency={currency}
              />
            </div>
          )}

          {/* Risk warnings */}
          {plan.risk_warnings?.length > 0 && (
            <div className="rounded-xl border border-amber-700/50 bg-amber-950/20 p-4">
              <h3 className="font-semibold text-amber-400 mb-2 text-sm">{t('debt.plan.warnings.title')}</h3>
              <ul className="space-y-1">
                {plan.risk_warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-300/80 flex items-start gap-2">
                    <span className="mt-0.5 flex-shrink-0">⚠️</span>
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Monthly projection table */}
          {plan.monthly_projection?.length > 0 && (
            <AmortizationTable projection={plan.monthly_projection} currency={currency} />
          )}

          {/* AI reasoning */}
          {plan.ai_reasoning && (
            <details className="card cursor-pointer group">
              <summary className="font-semibold text-slate-200 text-sm list-none flex items-center justify-between">
                {t('debt.plan.reasoning.title')}
                <span className="text-slate-500 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="text-slate-500 text-xs leading-relaxed mt-3">{plan.ai_reasoning}</p>
            </details>
          )}

          {/* Regenerate */}
          <div className="flex justify-end">
            <button
              onClick={() => { if (paid) setShowRegenModal(true); }}
              disabled={isGenerating || isUpdating || !paid}
              title={!paid ? t('plan.regenerate.pro_only') : undefined}
              className={`text-xs transition-colors ${
                paid
                  ? 'text-slate-500 hover:text-brand-400 cursor-pointer'
                  : 'text-slate-700 cursor-not-allowed'
              }`}
            >
              {isGenerating || isUpdating ? t('debt.plan.generating') : t('debt.plan.regenerate')}
            </button>
          </div>
        </>
      )}

      {/* Regenerate modal */}
      <Modal
        isOpen={showRegenModal}
        onClose={() => setShowRegenModal(false)}
        title={t('debt.plan.regen_modal.title')}
        size="md"
      >
        <DebtForm
          initialValues={goal}
          isSubmitting={isGenerating || isUpdating}
          onUseSame={handleRegenSame}
          onSubmit={handleRegenWithUpdate}
        />
      </Modal>
    </div>
  );
}
